"use client"

import type React from "react"
import { useEffect, useRef } from "react"

interface DashedRingProperties {
  size: number
  thickness: number
  dashColor: string
  activeColor: string
  dashCount: number
  animationDuration: number
  isAnimating: boolean
  className: string
}

/**
 * A customizable animated dashed ring component
 */
function DashedRing({
  size = 180,
  thickness = 3,
  dashColor = "#d3d3d3",
  activeColor = "#00c389",
  dashCount = 30,
  animationDuration = 5,
  isAnimating = true,
  className = "",
}: DashedRingProperties) {
  const containerReference = useRef<HTMLDivElement>(null)
  const uniqueId = useRef(`dashed-ring-${Math.random().toString(36).substr(2, 9)}`)

  const dashAngle = 360 / dashCount
  const dashLength = dashAngle * 0.5
  const halfCount = Math.floor(dashCount / 2)

  /**
   * Generates a string representing a conic gradient with alternating colored dashes and transparent spaces.
   */
  const generateDashes = (color: string): string => {
    const gradient: string[] = []
    for (let index = 0; index < dashCount; index += 1) {
      const start = index * dashAngle
      const end = start + dashLength
      gradient.push(`${color} ${start}deg ${end}deg`)
      gradient.push(`transparent ${end}deg ${start + dashAngle}deg`)
    }
    return gradient.join(", ")
  }

  /**
   * Generates reveal keyframes for the animation
   */
  const generateRevealKeyframes = (): string => {
    const keyframes: string[] = []
    const steps = halfCount
    const stepPercentage = 100 / steps

    keyframes.push(`
      0% {
        background: conic-gradient(${generateDashes(dashColor)});
      }
    `)

    for (let step = 1; step <= steps; step += 1) {
      const percentage = step * stepPercentage
      const gradient: string[] = []

      for (let dashIndex = 0; dashIndex < dashCount; dashIndex += 1) {
        const start = dashIndex * dashAngle
        const end = start + dashLength

        if (dashIndex < halfCount) {
          const color = dashIndex < halfCount - step ? dashColor : activeColor
          gradient.push(`${color} ${start}deg ${end}deg`)
        } else {
          gradient.push(`${dashColor} ${start}deg ${end}deg`)
        }
        gradient.push(`transparent ${end}deg ${start + dashAngle}deg`)
      }

      keyframes.push(`
        ${percentage}% {
          background: conic-gradient(${gradient.join(", ")});
        }
      `)
    }

    return keyframes.join("\n")
  }

  /**
   * Generates fill keyframes for the animation
   */
  const generateFillKeyframes = (): string => {
    const keyframes: string[] = []
    const steps = halfCount
    const stepPercentage = 100 / steps

    keyframes.push(`
      0% {
        background: conic-gradient(${generateDashes(dashColor)});
      }
    `)

    for (let step = 1; step <= steps; step += 1) {
      const percentage = step * stepPercentage
      const gradient: string[] = []

      for (let dashIndex = 0; dashIndex < dashCount; dashIndex += 1) {
        const start = dashIndex * dashAngle
        const end = start + dashLength

        if (dashIndex >= halfCount) {
          const fillIndex = dashIndex - halfCount
          const color = fillIndex < step ? activeColor : dashColor
          gradient.push(`${color} ${start}deg ${end}deg`)
        } else {
          gradient.push(`${dashColor} ${start}deg ${end}deg`)
        }
        gradient.push(`transparent ${end}deg ${start + dashAngle}deg`)
      }

      keyframes.push(`
        ${percentage}% {
          background: conic-gradient(${gradient.join(", ")});
        }
      `)
    }

    return keyframes.join("\n")
  }

  useEffect(() => {
    const revealAnimationName = `${uniqueId.current}-reveal`
    const fillAnimationName = `${uniqueId.current}-fill`

    const animationCSS = `
      @keyframes ${revealAnimationName} {
        ${generateRevealKeyframes()}
      }
      @keyframes ${fillAnimationName} {
        ${generateFillKeyframes()}
      }
    `

    const styleId = `${uniqueId.current}-styles`

    // Remove existing style if it exists
    const existingStyle = document.getElementById(styleId)
    if (existingStyle) {
      document.head.removeChild(existingStyle)
    }

    // Add new style
    const style = document.createElement("style")
    style.id = styleId
    style.innerHTML = animationCSS
    document.head.appendChild(style)

    // Update CSS custom properties for animations
    if (containerReference.current) {
      containerReference.current.style.setProperty("--reveal-animation", revealAnimationName)
      containerReference.current.style.setProperty("--fill-animation", fillAnimationName)
    }

    return () => {
  
      const styleElement = document.getElementById(styleId)
      if (styleElement) {
        document.head.removeChild(styleElement)
      }
    }
  }, [dashColor, activeColor, dashCount, animationDuration])

  const ringStyle: React.CSSProperties = {
    position: "absolute",
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: "50%",
    mask: `radial-gradient(farthest-side, transparent calc(100% - ${thickness}px), black 100%)`,
    WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${thickness}px), black 100%)`,
    pointerEvents: "none",
  }

  return (
    <div
      ref={containerReference}
      className={`dashed-ring-container ${className}`}
      style={{
        position: "relative",
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      <div
        className="dashed-ring-base"
        style={{
          ...ringStyle,
          background: `conic-gradient(${generateDashes(dashColor)})`,
        }}
      />
      <div
        className="dashed-ring-reveal"
        style={{
          ...ringStyle,
          background: `conic-gradient(${generateDashes(dashColor)})`,
          animation: isAnimating ? `var(--reveal-animation) ${animationDuration}s linear infinite` : "none",
        }}
      />
      <div
        className="dashed-ring-fill"
        style={{
          ...ringStyle,
          background: `conic-gradient(${generateDashes(dashColor)})`,
          animation: isAnimating ? `var(--fill-animation) ${animationDuration}s linear infinite` : "none",
        }}
      />
    </div>
  )
}

export default DashedRing
